<!-- Polaris (fase 5.6, R2-5-PC-9 = A): the company as a drawer over the companies list, the contact drawer's shape
     (fase 5.3) and the kit's CompanyPanel (areas/contacts/CompanyPanel.vue): tabs Details (the profile, always
     editable, and the danger zone), Contacts, History and Notes — what the woot's company page had, in the order of
     R2-5-PC-2 (the wireframe's Contacts | History | Notes after Details). It opens on Details, like the contact (the
     woot opened History because the profile was the page beside it). DrawerFrame (fase P, PROVISÓRIO): a column
     from 1280px, the non-modal overlay below (R2-X-3). Closing asks the page, and the page's route change is what the
     discard guard stops (R2-5-PC-4 = B). The company logic (contacts, search, link, remove, delete) is the woot's
     CompanyDetailView, moved here unchanged. -->
<script setup>
import { computed, onBeforeUnmount, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { createReusableTemplate } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { useCompaniesStore } from 'dashboard/stores/companies';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Policy from 'dashboard/components/policy.vue';
import CompanyContactsSidebar from 'dashboard/components-next/Companies/CompanyDetail/CompanyContactsSidebar.vue';
import CompanyHistorySidebar from 'dashboard/components-next/Companies/CompanyDetail/CompanyHistorySidebar.vue';
import CompanyNotesSidebar from 'dashboard/components-next/Companies/CompanyDetail/CompanyNotesSidebar.vue';
import CompanyProfileCard from 'dashboard/components-next/Companies/CompanyDetail/CompanyProfileCard.vue';
import ConfirmCompanyDeleteDialog from 'dashboard/components-next/Companies/CompanyDetail/ConfirmCompanyDeleteDialog.vue';
import DrawerFrame from 'dashboard/components-next/polaris/cards/DrawerFrame.vue';
import DiscardChangesAlert from 'dashboard/components-next/polaris/cards/DiscardChangesAlert.vue';
import { useDiscardGuard } from 'dashboard/components-next/polaris/cards/useDiscardGuard';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import { Skeleton } from 'dashboard/components-next/polaris/ui/skeleton';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from 'dashboard/components-next/polaris/ui/empty';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'dashboard/components-next/polaris/ui/tabs';

const props = defineProps({
  companyId: { type: [String, Number], default: null },
});

// close({ force }): the page closes the drawer (force: without the guard, after a delete).
const emit = defineEmits(['close']);

const { t } = useI18n();
const companiesStore = useCompaniesStore();
const [DefineWho, Who] = createReusableTemplate();

const confirmDeleteDialogRef = ref(null);
const selectedCandidate = ref(null);

const id = computed(() => Number(props.companyId) || null);
const open = computed(() => !!id.value);
const record = computed(() => companiesStore.getRecord(id.value));
// A list fetch replaces the store's records for a moment (another page, a sort): the drawer keeps the last record of
// the same company meanwhile, so the profile form does not unmount with what was typed.
const kept = ref(null);
watch(
  record,
  value => {
    if (value?.id) kept.value = value;
  },
  { immediate: true }
);
const company = computed(() => {
  if (record.value?.id) return record.value;
  return kept.value?.id === id.value ? kept.value : {};
});
const companyContacts = computed(() => companiesStore.companyContacts);
const companyContactsMeta = computed(() => companiesStore.companyContactsMeta);
const companyConversations = computed(
  () => companiesStore.companyConversations || []
);
const companyNotes = computed(() => companiesStore.companyNotes || []);
const contactSearchResults = computed(
  () => companiesStore.contactSearchResults
);
const uiFlags = computed(() => companiesStore.getUIFlags);

const isFetchingCompany = computed(() => uiFlags.value.fetchingItem);
const isFetchingContacts = computed(() => uiFlags.value.fetchingContacts);
const isFetchingConversations = computed(
  () => uiFlags.value.fetchingConversations
);
const isFetchingNotes = computed(() => uiFlags.value.fetchingNotes);
const isSearchingContacts = computed(() => uiFlags.value.searchingContacts);
const isManagingContacts = computed(
  () => uiFlags.value.creatingContact || uiFlags.value.removingContact
);
const isDeletingCompany = computed(() => uiFlags.value.deletingItem);
const hasCompany = computed(() => Boolean(company.value?.id));
const showInitialLoadingState = computed(
  () =>
    !hasCompany.value && (isFetchingCompany.value || isFetchingContacts.value)
);
const displayName = computed(
  () => company.value?.name || t('COMPANIES.UNNAMED')
);
const tabs = computed(() => [
  { value: 'details', label: t('CONTACTS_POLARIS.DRAWER.TABS.DETAILS') },
  { value: 'contacts', label: t('COMPANIES.DETAIL.SIDEBAR.TABS.CONTACTS') },
  { value: 'history', label: t('COMPANIES.DETAIL.SIDEBAR.TABS.HISTORY') },
  { value: 'notes', label: t('COMPANIES.DETAIL.SIDEBAR.TABS.NOTES') },
]);
const tab = ref('details');

const label = computed(() =>
  hasCompany.value
    ? t('COMPANIES_POLARIS.DRAWER.LABEL', { name: displayName.value })
    : t('COMPANIES_POLARIS.DRAWER.LABEL_EMPTY')
);

const clearSelectedCandidate = () => {
  selectedCandidate.value = null;
};

const onTabChange = value => {
  if (!id.value) return;
  if (value === 'notes') companiesStore.getCompanyNotes(id.value);
  if (value === 'history') companiesStore.getCompanyConversations(id.value);
};
watch(tab, onTabChange);

const loadCompanyContactsPage = async page => {
  if (!id.value) return;
  await companiesStore.getCompanyContacts(id.value, page);
};

const handleContactSearch = async query => {
  await companiesStore.searchCompanyContactCandidates({
    companyId: id.value,
    search: query,
  });
};

const handleConfirmContactSelection = async () => {
  const candidate = selectedCandidate.value;
  if (!candidate) return;

  const isReassigning =
    candidate.company?.id && candidate.company.id !== id.value;
  const message = isReassigning
    ? t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REASSIGN_SUCCESS')
    : t('COMPANIES.DETAIL.CONTACTS.MESSAGES.ADD_SUCCESS');

  try {
    await companiesStore.attachContactToCompany(id.value, candidate.id);
    useAlert(message);
    clearSelectedCandidate();
  } catch {
    const errorMessage = isReassigning
      ? t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REASSIGN_ERROR')
      : t('COMPANIES.DETAIL.CONTACTS.MESSAGES.ADD_ERROR');
    useAlert(errorMessage);
  }
};

const handleRemoveContact = async contactId => {
  const currentPage = Number(companyContactsMeta.value.page || 1);
  const nextPage =
    currentPage > 1 && companyContacts.value.length === 1
      ? currentPage - 1
      : currentPage;

  try {
    await companiesStore.removeContactFromCompany(
      id.value,
      contactId,
      nextPage
    );
    useAlert(t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REMOVE_SUCCESS'));
  } catch {
    useAlert(t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REMOVE_ERROR'));
  }
};

const openDeleteCompanyDialog = () => {
  confirmDeleteDialogRef.value?.dialogRef.open();
};

const handleDeleteCompany = async () => {
  try {
    await companiesStore.delete(id.value);
    useAlert(t('COMPANIES.DETAIL.DELETE.MESSAGES.SUCCESS'));
    confirmDeleteDialogRef.value?.dialogRef.close();
    emit('close', { force: true });
  } catch {
    useAlert(t('COMPANIES.DETAIL.DELETE.MESSAGES.ERROR'));
  }
};

// Another company starts on Details again and loads what the woot's page loaded.
watch(
  id,
  async value => {
    companiesStore.resetCompanyDetailState();
    clearSelectedCandidate();
    tab.value = 'details';
    if (!value) return;
    await Promise.allSettled([
      companiesStore.show(value),
      companiesStore.getCompanyContacts(value),
      companiesStore.getCompanyConversations(value),
    ]);
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  companiesStore.resetCompanyDetailState();
});

const profileRef = ref(null);
const isDirty = () => !!profileRef.value?.hasChanges;

const keepId = `company-drawer-keep-${useId()}`;
const { confirming, runGuarded, keepEditing, discard } = useDiscardGuard({
  isOpen: () => open.value,
  isDirty,
  close: () => {},
  keepId,
});

defineExpose({ runGuarded, isDirty });
</script>

<template>
  <DefineWho>
    <div v-if="hasCompany" class="flex min-w-0 items-center gap-3">
      <Avatar
        :name="displayName"
        :src="company.avatarUrl || ''"
        :size="40"
        hide-offline-status
        class="shrink-0"
      />
      <div class="grid min-w-0 flex-1">
        <p class="truncate type-polaris-card-title">{{ displayName }}</p>
        <p class="truncate type-polaris-caption text-polaris-muted-foreground">
          {{ company.domain }}
        </p>
      </div>
    </div>
  </DefineWho>

  <DrawerFrame
    :open="open"
    :label="label"
    column-class="w-[clamp(22rem,30%,26rem)]"
    overlay-class="md:max-w-[26rem]"
    data-company-drawer
    @close="emit('close', { force: false })"
  >
    <Tabs v-model="tab" class="flex min-h-0 flex-1 flex-col gap-0">
      <header
        class="flex h-[4.5rem] shrink-0 items-center gap-2 border-0 border-b border-solid border-polaris-border px-4 pe-12"
      >
        <!-- Four tabs, each as wide as its name, with a little less padding; the drawer starts at 22rem (not the
             contact's 20rem) so the four fit whole, and the contacts count stays in the tab's own heading. If a
             translation is longer, the list scrolls sideways instead of cutting names. -->
        <TabsList :aria-label="label" class="max-w-full overflow-x-auto">
          <TabsTrigger
            v-for="tabItem in tabs"
            :key="tabItem.value"
            :value="tabItem.value"
            class="flex-none px-1.5"
          >
            {{ tabItem.label }}
          </TabsTrigger>
        </TabsList>
      </header>

      <div v-if="confirming" class="p-4">
        <DiscardChangesAlert
          :keep-id="keepId"
          @keep="keepEditing"
          @discard="discard"
        />
      </div>

      <div
        v-if="showInitialLoadingState"
        class="grid gap-4 p-4"
        role="status"
        :aria-label="t('COMPANIES.DETAIL.LOADING')"
      >
        <div class="flex items-center gap-3">
          <Skeleton class="size-[72px] rounded-full" />
          <div class="grid flex-1 gap-2">
            <Skeleton class="h-4 w-2/3" />
            <Skeleton class="h-3 w-1/2" />
          </div>
        </div>
        <Skeleton v-for="row in 4" :key="row" class="h-8 w-full" />
      </div>

      <Empty v-else-if="!hasCompany" class="m-4 border">
        <EmptyHeader>
          <EmptyTitle>{{ t('COMPANIES.DETAIL.EMPTY_STATE.TITLE') }}</EmptyTitle>
          <EmptyDescription>
            {{ t('COMPANIES.DETAIL.EMPTY_STATE.SUBTITLE') }}
          </EmptyDescription>
        </EmptyHeader>
      </Empty>

      <template v-else>
        <!-- Details stays mounted on the other tabs: the profile keeps what was typed (the guard sees it). -->
        <TabsContent
          v-show="!confirming"
          value="details"
          force-mount
          class="min-h-0 flex-1 overflow-auto"
        >
          <div class="grid grid-cols-1 gap-6 p-4">
            <CompanyProfileCard
              :key="company.id"
              ref="profileRef"
              :company="company"
              :is-loading="isFetchingCompany"
            />
            <Policy :permissions="['administrator']">
              <section
                class="grid grid-cols-1 gap-3 border-0 border-t border-solid border-polaris-border pt-5"
                data-company-danger-zone
              >
                <div class="grid gap-1">
                  <h3 class="type-polaris-label text-polaris-foreground">
                    {{ t('COMPANIES.DETAIL.DELETE.SECTION_TITLE') }}
                  </h3>
                  <p class="type-polaris-caption text-polaris-muted-foreground">
                    {{ t('COMPANIES.DETAIL.DELETE.SECTION_DESCRIPTION') }}
                  </p>
                </div>
                <div>
                  <Button
                    variant="destructive"
                    size="sm"
                    :disabled="isDeletingCompany"
                    @click="openDeleteCompanyDialog"
                  >
                    {{ t('COMPANIES.DETAIL.DELETE.BUTTON') }}
                  </Button>
                </div>
              </section>
            </Policy>
          </div>
        </TabsContent>
        <TabsContent
          v-show="!confirming"
          value="contacts"
          class="min-h-0 flex-1 overflow-auto"
        >
          <div class="grid grid-cols-1 gap-4 py-4">
            <div class="px-4"><Who /></div>
            <CompanyContactsSidebar
              :company="company"
              :contacts="companyContacts"
              :meta="companyContactsMeta"
              :is-loading="isFetchingContacts"
              :is-busy="isManagingContacts"
              :search-results="contactSearchResults"
              :is-searching="isSearchingContacts"
              :selected-contact="selectedCandidate"
              @cancel-contact-selection="clearSelectedCandidate"
              @confirm-contact-selection="handleConfirmContactSelection"
              @search="handleContactSearch"
              @select-contact="contact => (selectedCandidate = contact)"
              @remove-contact="handleRemoveContact"
              @update:current-page="loadCompanyContactsPage"
            />
          </div>
        </TabsContent>
        <TabsContent
          v-show="!confirming"
          value="history"
          class="min-h-0 flex-1 overflow-auto"
        >
          <div class="grid grid-cols-1 gap-4 py-4">
            <div class="px-4"><Who /></div>
            <CompanyHistorySidebar
              :conversations="companyConversations"
              :is-loading="isFetchingConversations"
            />
          </div>
        </TabsContent>
        <TabsContent
          v-show="!confirming"
          value="notes"
          class="min-h-0 flex-1 overflow-auto"
        >
          <div class="grid grid-cols-1 gap-4 py-4">
            <div class="px-4"><Who /></div>
            <CompanyNotesSidebar
              :notes="companyNotes"
              :is-loading="isFetchingNotes"
            />
          </div>
        </TabsContent>
      </template>
    </Tabs>
    <ConfirmCompanyDeleteDialog
      ref="confirmDeleteDialogRef"
      :company="company"
      :is-loading="isDeletingCompany"
      @confirm="handleDeleteCompany"
    />
  </DrawerFrame>
</template>
